/**
 * SA / 社員 用 資料集 (v3.2.12 / SA-5, v3.2.28+ で PDF DL 型を追加対応)。
 *
 * URL: /sa/documents
 *
 * `documents.status === 'published'` のものだけ表示。
 * タイプ別の URL 生成:
 *   - 'viewer' (既定)     : 静的な公開URL (パスワード保護の場合あり) — そのままコピー
 *   - 'pdf_download'      : `issuePdfDownloadLink` で都度ユニークトークンを発行
 *     - 既に自分が発行した有効トークンがあれば「URL コピー」ボタン (そのトークンの URL を直接コピー)
 *     - なければ「DL リンク発行」ボタン (発行 → 自動コピー → 表示を URL コピーに切替)
 *     - トークンが期限切れまたは admin に削除されると、自動的に「DL リンク発行」に戻る
 */
import { ClipboardCopy, FileDown, FileText, Loader2 } from 'lucide-react';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  type DocumentRecord,
  type MyActivePdfTokenInfo,
  useDocuments,
  useIssuePdfDownloadLink,
  useMyActivePdfTokens,
} from '@/hooks/useDocuments';

export function SaDocumentsPage() {
  const docs = useDocuments();
  const issueLink = useIssuePdfDownloadLink();
  const myTokens = useMyActivePdfTokens();

  const published = useMemo(() => {
    return (docs.data ?? []).filter(
      // v3.2.28+: visibleToSa が明示的に false の場合だけ除外
      (d) => d.status === 'published' && d.visibleToSa !== false,
    );
  }, [docs.data]);

  const tokensByDocId = myTokens.data ?? {};

  const handleCopy = async (url: string, message = 'URL をコピーしました') => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(message);
    } catch {
      toast.error('コピーに失敗しました');
    }
  };

  const handleIssuePdfLink = async (doc: DocumentRecord) => {
    try {
      const r = await issueLink.mutateAsync({ documentId: doc.id });
      try {
        await navigator.clipboard.writeText(r.url);
        toast.success('ダウンロードリンクを生成しコピーしました');
      } catch {
        toast.success('ダウンロードリンクを生成しました', {
          description: r.url,
        });
      }
    } catch (e) {
      toast.error(`発行失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <Screen
      title="資料集"
      description="現在「公開中」の資料一覧。顧客への共有 URL をコピーして使用してください。"
    >
      {docs.isLoading ? (
        <Spinner />
      ) : published.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="公開中の資料がありません"
          description="管理者が資料を公開するとここに表示されます。"
        />
      ) : (
        <div className="space-y-2">
          {published.map((d) => {
            const isPdf = d.type === 'pdf_download';
            const myToken: MyActivePdfTokenInfo | undefined = isPdf
              ? tokensByDocId[d.id]
              : undefined;
            const staticUrl = isPdf
              ? `${window.location.origin}/dl/${d.slug}`
              : `${window.location.origin}/docs/${d.slug}`;
            // PDF 型 + 自分の有効トークンがあれば、それをそのまま「URL コピー」値に使う
            const effectiveUrl = myToken?.url ?? staticUrl;
            return (
              <div
                key={d.id}
                className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3 flex items-center gap-3"
              >
                {isPdf ? (
                  <FileDown className="size-4 text-blue-600 shrink-0" />
                ) : (
                  <FileText className="size-4 text-[var(--text-muted)] shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-1.5">
                    {d.title}
                    {isPdf && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-normal">
                        PDF ダウンロード
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--text-muted)] truncate font-mono">
                    {isPdf && myToken
                      ? myToken.url
                      : staticUrl + (isPdf ? '?t=（クリックで発行）' : '')}
                  </div>
                  {!isPdf && d.passwordHint && (
                    <div className="text-[10px] text-[var(--accent-amber,_#d4a373)] mt-0.5">
                      ※ パスワード保護: {d.passwordHint}
                    </div>
                  )}
                  {isPdf && myToken && (
                    <div className="text-[10px] text-green-700 mt-0.5">
                      ✓ 発行済み
                      {myToken.expiresAt && (
                        <span className="ml-2 text-[var(--text-muted)]">
                          (期限: {formatLinkExpiry(myToken.expiresAt)})
                        </span>
                      )}
                      {!myToken.expiresAt && (
                        <span className="ml-2 text-[var(--text-muted)]">
                          (無期限)
                        </span>
                      )}
                    </div>
                  )}
                  {isPdf && !myToken && d.tokenValidityHours && (
                    <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                      発行後の有効期限: {d.tokenValidityHours} 時間
                    </div>
                  )}
                  {isPdf && !myToken && d.tokenValidityHours === null && (
                    <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                      発行後の有効期限: 無期限
                    </div>
                  )}
                </div>
                {isPdf && !myToken ? (
                  // 未発行: 発行ボタン
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={() => void handleIssuePdfLink(d)}
                    disabled={issueLink.isPending}
                  >
                    {issueLink.isPending ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <ClipboardCopy className="size-3.5" />
                    )}
                    DL リンク発行
                  </Button>
                ) : (
                  // 既発行 (PDF) または viewer タイプ: URL コピー
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => void handleCopy(effectiveUrl)}
                  >
                    <ClipboardCopy className="size-3.5" />
                    URL コピー
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Screen>
  );
}

function formatLinkExpiry(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return iso;
  }
}
